import React, { useState, useEffect } from 'react';
import {
  Save,
  RotateCcw,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  FileText,
  Settings,
  User,
  Grid
} from 'react-feather';
import { useAuth } from '../../contexts/AuthContext';

interface PromptsConfig {
  interface: {
    introductoryMessage: {
      greeting: string;
      capabilities: string;
      prompt: string;
      fullMessage: string;
    };
  };
  system: {
    identity: string;
    responseFormat: {
      description: string;
      template: string;
    };
    contentGuidelines: any[];
    keyPrinciples: string[];
    platformDescription: string;
  };
  user: {
    contextPrompt: string;
    fallbackResponses: {
      noContext: string;
      apiError: string;
      noResponse: string;
    };
  };
  contextBuilding: {
    prefix: string;
    sourceTemplate: string;
    truncationMessage: string;
  };
}

type TabKey = 'interface' | 'system' | 'user' | 'contextBuilding';

const Prompts: React.FC = () => {
  const { user } = useAuth();
  const canManagePrompts = user?.capabilities?.canUseTeaching || false;
  const [config, setConfig] = useState<PromptsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('interface');
  const [hasChanges, setHasChanges] = useState(false);

  // Load prompts configuration
  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/admin/prompts');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setConfig(data);
      setHasChanges(false);
    } catch (err) {
      console.error('Error loading prompts config:', err);
      setError(err instanceof Error ? err.message : 'Failed to load prompts configuration');
    } finally {
      setLoading(false);
    }
  };

  // Save prompts configuration
  const saveConfig = async () => {
    if (!config) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const response = await fetch('/api/admin/prompts', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      setSuccess(result.message || 'Prompts configuration saved successfully');
      setHasChanges(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving prompts config:', err);
      setError(err instanceof Error ? err.message : 'Failed to save prompts configuration');
    } finally {
      setSaving(false);
    }
  };

  // Reset configuration
  const resetConfig = () => {
    if (hasChanges && !confirm('Are you sure you want to discard all changes?')) {
      return;
    }
    loadConfig();
  };

  // Update configuration
  const updateConfig = (section: TabKey, updates: any) => {
    if (!config) return;
    
    setConfig(prev => ({
      ...prev!,
      [section]: {
        ...prev![section],
        ...updates
      }
    }));
    setHasChanges(true);
  };

  // Update nested configuration
  const updateNestedConfig = (section: TabKey, path: string[], value: any) => {
    if (!config) return;
    
    setConfig(prev => {
      const newConfig = { ...prev! };
      let current = newConfig[section] as any;
      
      // Navigate to the parent of the target property
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      
      // Set the target property
      current[path[path.length - 1]] = value;
      
      return newConfig;
    });
    setHasChanges(true);
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const tabs = [
    { key: 'interface' as TabKey, label: 'Interface', icon: <FileText size={16} /> },
    { key: 'system' as TabKey, label: 'System', icon: <Settings size={16} /> },
    { key: 'user' as TabKey, label: 'User Context', icon: <User size={16} /> },
    { key: 'contextBuilding' as TabKey, label: 'Context Building', icon: <Grid size={16} /> }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={16} />
          <span>Loading prompts configuration...</span>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
        <h3 className="text-lg font-semibold mb-2">Configuration Not Found</h3>
        <p className="text-gray-600 mb-4">Unable to load prompts configuration</p>
        <button
          onClick={loadConfig}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Prompts Management</h1>
        <div className="flex items-center space-x-3">
          {hasChanges && (
            <span className="text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded">
              Unsaved changes
            </span>
          )}
          {canManagePrompts ? (
            <button
              onClick={resetConfig}
              disabled={!hasChanges}
              className="flex items-center space-x-2 px-3 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCcw size={16} />
              <span>Reset</span>
            </button>
          ) : (
            <div className="flex items-center space-x-2 px-3 py-2 text-gray-400 bg-gray-50 rounded cursor-not-allowed">
              <RotateCcw size={16} />
              <span>Reset</span>
            </div>
          )}
          {canManagePrompts ? (
            <button
              onClick={saveConfig}
              disabled={saving || !hasChanges}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <RefreshCw size={16} className="animate-spin" />
              ) : (
                <Save size={16} />
              )}
              <span>{saving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          ) : (
            <div className="flex items-center space-x-2 px-4 py-2 bg-gray-300 text-gray-500 rounded cursor-not-allowed">
              <Save size={16} />
              <span>Admin Access Required</span>
            </div>
          )}
        </div>
      </div>

      {!canManagePrompts && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="text-amber-500" size={16} />
            <span className="text-amber-700">
              You have read-only access to prompts configuration. Admin role required for modifications.
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="text-red-500" size={16} />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="text-green-500" size={16} />
            <span className="text-green-700">{success}</span>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg border p-6">
        {activeTab === 'interface' && (
          <InterfaceTab 
            config={config.interface} 
            onChange={(updates) => updateConfig('interface', updates)}
            onNestedChange={(path, value) => updateNestedConfig('interface', path, value)}
            readOnly={!canManagePrompts}
          />
        )}
        {activeTab === 'system' && (
          <SystemTab 
            config={config.system} 
            onChange={(updates) => updateConfig('system', updates)}
            onNestedChange={(path, value) => updateNestedConfig('system', path, value)}
            readOnly={!canManagePrompts}
          />
        )}
        {activeTab === 'user' && (
          <UserTab 
            config={config.user} 
            onChange={(updates) => updateConfig('user', updates)}
            onNestedChange={(path, value) => updateNestedConfig('user', path, value)}
            readOnly={!canManagePrompts}
          />
        )}
        {activeTab === 'contextBuilding' && (
          <ContextBuildingTab 
            config={config.contextBuilding} 
            onChange={(updates) => updateConfig('contextBuilding', updates)}
            readOnly={!canManagePrompts}
          />
        )}
      </div>
    </div>
  );
};

// Interface Tab Component
const InterfaceTab: React.FC<{
  config: PromptsConfig['interface'];
  onChange: (updates: any) => void;
  onNestedChange: (path: string[], value: any) => void;
  readOnly: boolean;
}> = ({ config, onNestedChange, readOnly }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Introductory Message</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Greeting
            </label>
            <input
              type="text"
              value={config.introductoryMessage.greeting}
              onChange={(e) => onNestedChange(['introductoryMessage', 'greeting'], e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter greeting message"
              readOnly={readOnly}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Capabilities
            </label>
            <textarea
              value={config.introductoryMessage.capabilities}
              onChange={(e) => onNestedChange(['introductoryMessage', 'capabilities'], e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe what the assistant can help with"
              readOnly={readOnly}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prompt
            </label>
            <input
              type="text"
              value={config.introductoryMessage.prompt}
              onChange={(e) => onNestedChange(['introductoryMessage', 'prompt'], e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter the prompt question"
              readOnly={readOnly}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Message (Auto-generated)
            </label>
            <textarea
              value={config.introductoryMessage.fullMessage}
              onChange={(e) => onNestedChange(['introductoryMessage', 'fullMessage'], e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Complete introductory message"
              readOnly={readOnly}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// System Tab Component
const SystemTab: React.FC<{
  config: PromptsConfig['system'];
  onChange: (updates: any) => void;
  onNestedChange: (path: string[], value: any) => void;
  readOnly: boolean;
}> = ({ config, onChange, onNestedChange, readOnly: _readOnly }) => {
  void _readOnly; // Available for disabling form inputs
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Identity
        </label>
        <textarea
          value={config.identity}
          onChange={(e) => onChange({ identity: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Define the assistant's identity and role"
        />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Response Format</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={config.responseFormat.description}
              onChange={(e) => onNestedChange(['responseFormat', 'description'], e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe the response format"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template
            </label>
            <textarea
              value={config.responseFormat.template}
              onChange={(e) => onNestedChange(['responseFormat', 'template'], e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder="HTML template for responses"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Content Guidelines
        </label>
        <textarea
          value={JSON.stringify(config.contentGuidelines, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              onChange({ contentGuidelines: parsed });
            } catch (err) {
              // Invalid JSON, don't update
            }
          }}
          rows={12}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
          placeholder="Content guidelines (JSON format)"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Key Principles
        </label>
        <textarea
          value={JSON.stringify(config.keyPrinciples, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              onChange({ keyPrinciples: parsed });
            } catch (err) {
              // Invalid JSON, don't update
            }
          }}
          rows={8}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
          placeholder="Key principles (JSON format)"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Platform Description
        </label>
        <textarea
          value={config.platformDescription}
          onChange={(e) => onChange({ platformDescription: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Describe the platform the assistant supports"
        />
      </div>
    </div>
  );
};

// User Tab Component
const UserTab: React.FC<{
  config: PromptsConfig['user'];
  onChange: (updates: any) => void;
  onNestedChange: (path: string[], value: any) => void;
  readOnly: boolean;
}> = ({ config, onChange, onNestedChange, readOnly: _readOnly }) => {
  void _readOnly; // Available for disabling form inputs
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Context Prompt
        </label>
        <textarea
          value={config.contextPrompt}
          onChange={(e) => onChange({ contextPrompt: e.target.value })}
          rows={8}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
          placeholder="Template for user context prompt"
        />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Fallback Responses</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              No Context
            </label>
            <input
              type="text"
              value={config.fallbackResponses.noContext}
              onChange={(e) => onNestedChange(['fallbackResponses', 'noContext'], e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Response when no context is found"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Error
            </label>
            <input
              type="text"
              value={config.fallbackResponses.apiError}
              onChange={(e) => onNestedChange(['fallbackResponses', 'apiError'], e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Response when API error occurs"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              No Response
            </label>
            <input
              type="text"
              value={config.fallbackResponses.noResponse}
              onChange={(e) => onNestedChange(['fallbackResponses', 'noResponse'], e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Response when no response can be generated"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Context Building Tab Component
const ContextBuildingTab: React.FC<{
  config: PromptsConfig['contextBuilding'];
  onChange: (updates: any) => void;
  readOnly: boolean;
}> = ({ config, onChange, readOnly: _readOnly }) => {
  void _readOnly; // Available for disabling form inputs
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Prefix
        </label>
        <textarea
          value={config.prefix}
          onChange={(e) => onChange({ prefix: e.target.value })}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Prefix for context building"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Source Template
        </label>
        <textarea
          value={config.sourceTemplate}
          onChange={(e) => onChange({ sourceTemplate: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
          placeholder="Template for source formatting"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Truncation Message
        </label>
        <input
          type="text"
          value={config.truncationMessage}
          onChange={(e) => onChange({ truncationMessage: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Message shown when content is truncated"
        />
      </div>
    </div>
  );
};

export default Prompts;